import React, { createContext, useContext, useCallback, useEffect, ReactNode } from 'react';
import { useDomainFetch } from './hooks/useDomainFetch';
import { useAuth } from './AuthContext';
import { useSettings } from './SettingsContext';
import { FarmAsset } from '../types';
import { API_BASE_URL } from '../config/api';

interface ConfigurationContextType {
  // Data
  assets: FarmAsset[] | null;

  // State
  loading: boolean;
  error: string | null;
  isStale: boolean;
  lastFetch: number | null;

  // Actions
  refetch: () => Promise<void>;
}

export const ConfigurationContext = createContext<ConfigurationContextType | undefined>(undefined);
export type { ConfigurationContextType };

interface ConfigurationProviderProps {
  children: ReactNode;
}

export function ConfigurationProvider({ children }: ConfigurationProviderProps) {
  const { refreshToken, logout } = useAuth();
  const { settings } = useSettings();

  const getToken = useCallback(
    () => (typeof window !== 'undefined' ? localStorage.getItem('tb_token') : null),
    [],
  );

  const onUnauthorized = useCallback(
    async (makeRequest: () => Promise<Response>): Promise<Response> => {
      const refreshTokenValue =
        typeof window !== 'undefined' ? localStorage.getItem('tb_refresh_token') : null;
      if (refreshTokenValue) {
        const refreshSuccess = await refreshToken();
        if (refreshSuccess) {
          return makeRequest();
        } else {
          logout();
        }
      } else {
        logout();
      }
      return new Response(null, { status: 401 });
    },
    [refreshToken, logout],
  );

  // --- assets (farm locations) -- slow refresh as they're mostly static ---
  const {
    state: assetsState,
    refetch: refetchAssets,
  } = useDomainFetch<FarmAsset[]>({
    endpoint: `${API_BASE_URL}/api/assets`,
    refreshInterval: settings.refreshIntervals.assets,
    getToken,
    onUnauthorized,
  });

  // Initial fetch on mount
  useEffect(() => {
    if (assetsState.isStale || !assetsState.lastFetch) {
      refetchAssets();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Re-fetch when stale
  useEffect(() => {
    if (assetsState.isStale && assetsState.lastFetch) {
      refetchAssets();
    }
  }, [assetsState.isStale, assetsState.lastFetch, refetchAssets]);

  const refetch = useCallback(async (): Promise<void> => {
    await refetchAssets(true);
  }, [refetchAssets]);

  const contextValue: ConfigurationContextType = {
    assets: assetsState.data,
    loading: assetsState.loading,
    error: assetsState.error,
    isStale: assetsState.isStale,
    lastFetch: assetsState.lastFetch,
    refetch,
  };

  return (
    <ConfigurationContext.Provider value={contextValue}>
      {children}
    </ConfigurationContext.Provider>
  );
}

export function useConfiguration(): ConfigurationContextType {
  const context = useContext(ConfigurationContext);
  if (context === undefined) {
    throw new Error('useConfiguration must be used within a ConfigurationProvider');
  }
  return context;
}

export default ConfigurationProvider;
